import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { RotateCcw, Loader2, Plus, X, AlertTriangle, DollarSign, Upload, FileCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { CORE_STATUSES, CORE_NEXT, ESCALATION } from "./coreConstants";
import { formatCurrency, formatDateTime } from "./inventoryConstants";

const fileToB64 = (file, cb) => { const r = new FileReader(); r.onload = (e) => cb(e.target.result); r.readAsDataURL(file); };

const SummaryCard = ({ label, value, accent, testid, big }) => (
  <div className={`rounded-xl border p-4 min-w-0 ${big ? "bg-gradient-to-br from-red-600 to-red-700 text-white border-red-700" : "bg-white border-slate-200"}`} data-testid={testid}>
    <div className={`text-xs font-medium truncate ${big ? "text-red-100" : "text-slate-500"}`}>{label}</div>
    <div className={`font-bold mt-1 tabular-nums truncate ${big ? "text-2xl sm:text-3xl" : `text-xl sm:text-2xl ${accent || "text-slate-900"}`}`}>{value}</div>
  </div>
);

const CoreDetailModal = ({ coreId, onClose, onChanged, canManage, currentUser }) => {
  const [core, setCore] = useState(null);
  const [notes, setNotes] = useState("");
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";
  const load = () => { if (coreId) inventoryApi.getCore(coreId).then((c) => { setCore(c); setNotes(c.notes || ""); }).catch(() => {}); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [coreId]);
  if (!coreId || !core) return null;
  const st = CORE_STATUSES[core.status] || {};
  const esc = ESCALATION[core.escalation] || ESCALATION.ok;
  const nexts = CORE_NEXT[core.status] || [];

  const change = async (patch, msg) => {
    try { await inventoryApi.updateCore(core.id, { ...patch, updated_by: userName }); toast.success(msg || "Updated"); load(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const upload = (field, file) => {
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    fileToB64(file, (b64) => change({ [field]: b64 }, "Document attached"));
  };

  return (
    <Dialog open={!!coreId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="core-detail-modal">
        <DialogTitle className="sr-only">{core.core_number}</DialogTitle>
        <DialogDescription className="sr-only">Core record details and status workflow</DialogDescription>
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">{core.core_number}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color}`}>{st.label}</span>
            </div>
            {core.at_risk && <div className={`text-xs mt-1 ${esc.color}`}>{esc.label} · {core.days_open} days open</div>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="flex items-center justify-between bg-red-50 rounded-lg p-3">
            <span className="text-sm text-slate-600">Core Charge</span>
            <span className="text-lg font-bold text-red-700">{formatCurrency(core.core_charge)}</span>
          </div>
          <div className="text-sm divide-y divide-slate-50">
            {[["Vendor", core.vendor_name], ["Invoice #", core.invoice_number], ["Unit", core.unit_number], ["Part #", core.part_number],
              ["Purchase Date", formatDateTime(core.purchase_date)], ["Due Date", formatDateTime(core.due_date)],
              ["Return Date", core.return_date ? formatDateTime(core.return_date) : "—"], ["Credit Date", core.credit_date ? formatDateTime(core.credit_date) : "—"]]
              .map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5"><span className="text-slate-500">{k}</span><span className="text-slate-900 font-medium text-right">{v || "—"}</span></div>
              ))}
          </div>

          {canManage && (
            <div className="grid grid-cols-2 gap-2">
              <label className={`flex items-center justify-center gap-1 text-xs py-2 rounded-lg border cursor-pointer ${core.return_receipt_upload ? "border-green-300 text-green-700 bg-green-50" : "border-slate-200 text-blue-600"}`}>
                {core.return_receipt_upload ? <FileCheck className="w-4 h-4" /> : <Upload className="w-4 h-4" />} Return Receipt
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload("return_receipt_upload", e.target.files[0])} data-testid="upload-return-receipt" />
              </label>
              <label className={`flex items-center justify-center gap-1 text-xs py-2 rounded-lg border cursor-pointer ${core.credit_memo_upload ? "border-green-300 text-green-700 bg-green-50" : "border-slate-200 text-blue-600"}`}>
                {core.credit_memo_upload ? <FileCheck className="w-4 h-4" /> : <Upload className="w-4 h-4" />} Credit Memo
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload("credit_memo_upload", e.target.files[0])} data-testid="upload-credit-memo" />
              </label>
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManage} />
            {canManage && <Button variant="outline" size="sm" className="mt-1" onClick={() => change({ notes }, "Notes saved")}>Save Notes</Button>}
          </div>
        </div>

        {canManage && nexts.length > 0 && (
          <div className="px-5 py-4 border-t border-slate-200 flex-shrink-0 flex flex-wrap gap-2">
            {nexts.map((s) => (
              <Button key={s} size="sm" variant={s === "lost" ? "outline" : "default"} onClick={() => change({ status: s }, `Marked ${CORE_STATUSES[s]?.label}`)} data-testid={`core-status-${s}-btn`}>
                {CORE_STATUSES[s]?.label} <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AddCoreModal = ({ open, onClose, vendors, vehicles, currentUser, onSaved }) => {
  const [form, setForm] = useState({ vendor_id: "", invoice_number: "", unit_id: "", part_number: "", core_charge: 0, notes: "" });
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";
  useEffect(() => { if (open) setForm({ vendor_id: "", invoice_number: "", unit_id: "", part_number: "", core_charge: 0, notes: "" }); }, [open]);
  const save = async () => {
    if (!Number(form.core_charge)) { toast.error("Core charge required"); return; }
    const unit = vehicles.find((v) => v.id === form.unit_id);
    try {
      await inventoryApi.createCore({ ...form, core_charge: Number(form.core_charge), unit_number: unit?.unit_number, created_by: userName });
      toast.success("Core record created"); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-core-modal">
        <DialogTitle className="sr-only">Add Core Record</DialogTitle>
        <DialogDescription className="sr-only">Create a new core charge record</DialogDescription>
        <h3 className="text-lg font-semibold mb-3">Add Core Record</h3>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Vendor</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
              <option value="">Select vendor...</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Invoice #</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Core Charge ($)</Label><Input type="number" step="0.01" value={form.core_charge} onChange={(e) => setForm({ ...form, core_charge: e.target.value })} data-testid="core-charge-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Unit</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}>
                <option value="">None</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unit_number}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-slate-500">Part #</Label><Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button><Button className="flex-1" onClick={save} data-testid="core-save">Save</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CoreTrackingSection = ({ currentUser, canManage, vehicles }) => {
  const [cores, setCores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [escFilter, setEscFilter] = useState("");
  const [vendors, setVendors] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchCores = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (escFilter) params.escalation = escFilter;
      const [c, s] = await Promise.all([inventoryApi.getCores(params), inventoryApi.getCoresSummary()]);
      setCores(c); setSummary(s);
    } catch { toast.error("Failed to load cores"); }
    setLoading(false);
  }, [statusFilter, escFilter]);

  useEffect(() => { inventoryApi.getVendors().then(setVendors).catch(() => {}); }, []);
  useEffect(() => { fetchCores(); }, [fetchCores]);

  return (
    <div className="space-y-4" data-testid="core-tracking-section">
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard big label="Core Money At Risk" value={formatCurrency(summary.money_at_risk)} testid="core-money-at-risk" />
          <SummaryCard label="Open Cores" value={summary.open_count} accent="text-amber-600" testid="core-open-count" />
          <SummaryCard label="Awaiting Credit" value={`${summary.awaiting_credit_count} · ${formatCurrency(summary.awaiting_credit_amount)}`} accent="text-indigo-600" />
          <SummaryCard label="Escalations >60d" value={summary.escalation_count} accent="text-red-600" testid="core-escalation-count" />
        </div>
      )}
      {summary && (summary.warning_count + summary.high_count + summary.escalation_count) > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {summary.warning_count > 0 && <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">{summary.warning_count} warning (&gt;14d)</span>}
          {summary.high_count > 0 && <span className="px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200">{summary.high_count} high (&gt;30d)</span>}
          {summary.escalation_count > 0 && <span className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-300">{summary.escalation_count} escalation (&gt;60d)</span>}
          {summary.lost_count > 0 && <span className="px-2 py-1 rounded bg-red-100 text-red-800">{summary.lost_count} lost · {formatCurrency(summary.lost_amount)}</span>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="core-status-filter">
            <option value="">All Statuses</option>
            {Object.entries(CORE_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={escFilter} onChange={(e) => setEscFilter(e.target.value)} data-testid="core-esc-filter">
            <option value="">All Escalations</option>
            <option value="warning">Warning (&gt;14d)</option>
            <option value="high">High (&gt;30d)</option>
            <option value="escalation">Escalation (&gt;60d)</option>
          </select>
        </div>
        {canManage && <Button onClick={() => setShowAdd(true)} data-testid="add-core-btn"><Plus className="w-4 h-4 mr-1" /> Add Core</Button>}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : cores.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><RotateCcw className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No core records</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {cores.map((c) => {
            const st = CORE_STATUSES[c.status] || {};
            const esc = ESCALATION[c.escalation] || ESCALATION.ok;
            return (
              <button key={c.id} onClick={() => setDetailId(c.id)} data-testid={`core-card-${c.id}`} className={`w-full text-left rounded-xl border p-4 hover:shadow-md transition-all ${c.at_risk ? `${esc.bg} ${esc.border} ${esc.ring}` : "bg-white border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{c.core_number}</span>
                    {c.escalation === "escalation" && <AlertTriangle className="w-4 h-4 text-red-500" />}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color}`}>{st.label}</span>
                </div>
                <div className="text-sm text-slate-500 mt-1">{c.vendor_name || "—"}{c.part_number ? ` · ${c.part_number}` : ""}{c.unit_number ? ` · Unit ${c.unit_number}` : ""}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-base font-bold text-red-700 inline-flex items-center"><DollarSign className="w-4 h-4" />{formatCurrency(c.core_charge).replace("$", "")}</span>
                  {c.at_risk && <span className={`text-xs font-medium ${esc.color}`}>{c.days_open}d open</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <CoreDetailModal coreId={detailId} onClose={() => setDetailId(null)} onChanged={fetchCores} canManage={canManage} currentUser={currentUser} />
      <AddCoreModal open={showAdd} onClose={() => setShowAdd(false)} vendors={vendors} vehicles={vehicles} currentUser={currentUser} onSaved={fetchCores} />
    </div>
  );
};
